import { TERMINAL_AUTH_ERROR_CODES } from "@/domain/auth";
import {
  getLocalStorageService,
  getLogger,
  getMonitoring,
  getSessionExpiredNotifier,
  getSessionStorageService,
  getTokenManager,
} from "@/infrastructure/di/services";
import { STORAGE_KEYS } from "@/infrastructure/storage/constants";
import { apiConfig } from "./config";
import { client } from "./generated/client.gen";
import { fetchWithTimeout } from "./sdkFetch";

/**
 * Runtime configuration for the generated Hey API client. Importing this
 * module (side effect) wires auth, org context, timeouts and the
 * session-expired flow into every SDK call. It must be imported before any
 * repository executes a request — `main.tsx` and the Vitest setup do so.
 */

// Generated SDK urls already include /api/v1, while VITE_API_BASE_URL
// historically carries it — strip the suffix to avoid doubling the prefix.
const baseUrl = apiConfig.baseUrl.replace(/\/api\/v1\/?$/, "");

const NO_TOKEN_ENDPOINTS = ["/api/v1/auth/login", "/api/v1/auth/refresh", "/api/v1/auth/logout"];

function isAuthEndpoint(url: string): boolean {
  const pathname = new URL(url, baseUrl || window.location.origin).pathname;
  return NO_TOKEN_ENDPOINTS.some((endpoint) => pathname.startsWith(endpoint));
}

let sessionExpiredHandled = false;

/** Re-arm the session-expired flow (called after a successful login). */
export function resetSdkSessionState(): void {
  sessionExpiredHandled = false;
}

async function handleSessionExpired(): Promise<void> {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;

  const localStore = getLocalStorageService();
  localStore.remove(STORAGE_KEYS.AUTH_TOKEN);
  localStore.remove(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
  localStore.remove(STORAGE_KEYS.USER_DATA);
  getSessionStorageService().clear();
  getMonitoring().clearUserContext();
  getSessionExpiredNotifier().notify("Your session has expired. Please log in again.");
}

function isTerminalAuthCode(code: unknown): boolean {
  return (
    typeof code === "string" && (TERMINAL_AUTH_ERROR_CODES as readonly string[]).includes(code)
  );
}

async function readErrorCode(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.clone().json();
    return typeof body === "object" && body !== null
      ? (body as Record<string, unknown>).error
      : undefined;
  } catch {
    return undefined;
  }
}

client.setConfig({
  auth: async () => {
    try {
      return (await getTokenManager().ensureValidToken()) ?? undefined;
    } catch (error) {
      // Let the server answer 401; the response interceptor recovers or logs out.
      getLogger().debug("Could not get token for request:", error);
      return getLocalStorageService().get<string>(STORAGE_KEYS.AUTH_TOKEN) ?? undefined;
    }
  },
  baseUrl,
  fetch: fetchWithTimeout,
});

// Pristine clones of each outgoing request so a 401 can be retried after a
// token refresh (the original body stream is consumed by the first attempt).
const retryableRequests = new WeakMap<Request, Request>();

client.interceptors.request.use((request) => {
  const orgUuid = getLocalStorageService().get<string>(STORAGE_KEYS.CURRENT_ORG_UUID);
  if (orgUuid) {
    request.headers.set("X-Organization-UUID", orgUuid);
  }
  retryableRequests.set(request, request.clone());
  return request;
});

client.interceptors.response.use(async (response, request) => {
  if (response.ok || isAuthEndpoint(request.url)) return response;

  // Terminal 403 (e.g. membership revoked): the session's org context is gone.
  if (response.status === 403) {
    if (isTerminalAuthCode(await readErrorCode(response))) {
      await handleSessionExpired();
    }
    return response;
  }

  if (response.status !== 401) return response;

  // Terminal 401: refresh token revoked/expired — no point retrying.
  if (isTerminalAuthCode(await readErrorCode(response))) {
    await handleSessionExpired();
    return response;
  }

  // Transient 401: refresh the token and retry the request once.
  const pristine = retryableRequests.get(request);
  let newToken: string | null = null;
  try {
    newToken = await getTokenManager().forceRefresh();
  } catch (error) {
    getLogger().error("SDK client: token refresh failed:", error);
  }
  if (!newToken || !pristine) {
    await handleSessionExpired();
    return response;
  }

  const retry = new Request(pristine);
  retry.headers.set("Authorization", `Bearer ${newToken}`);
  const retryResponse = await fetchWithTimeout(retry);
  if (retryResponse.status === 401) {
    await handleSessionExpired();
  }
  return retryResponse;
});
